// byGroupVisuals — pure presentation helper, deliberately scoped to the NEW by-cell
// pieces only (never imported by the untouched /monitoring screen). The palette is
// the Stitch mockups' exact hex tokens (stitch-dashboard.html /
// stitch-machine-card.html Tailwind config), per the PO's directive to follow the
// Stitch design closely; the same values live as CSS custom properties in byGroup.css
// (this JS mirror exists for the few places that need the colour as a prop, e.g.
// SVG accents).
import { RESIDUAL_BUCKET, normalizeSunBucket } from './plantMonitoringUtils'

// PO correction (2026-07-07): every bucket TITLE is the same state name (WORKING / IDLE /
// ERROR / OFF) so chips, the detail pill, quick filters and KPI titles all read EXACTLY the
// same everywhere — total consistency. Story 204 (S76, decision 14): that name is the
// group's CODE, not visible text — the card and the detail translate it by code like any
// other status (`statusDisplay` returns it as `statusCode`, domain 'StatusBucket').
// NON_OPERATIVE is a device MODE, not a status (see IcoStatus.jsx's JSDoc); the
// fourth visible STATUS is OFF, so the residual bucket displays as the literal
// 'OFF'. The internal identifiers stay WORKING/IDLE/ERROR/NON_OPERATIVE
// (RESIDUAL_BUCKET) — the latter matches sun's `nonOperativeCount` snapshot KPI field
// — this is a presentation-only change. Only
// PlantKpiBar's two non-state tiles (Total assets, Critical alarms) keep a
// translated label.
const BUCKET_META = {
  WORKING: { color: '#7ae0a9', railClass: 'mcx-rail-green', label: 'WORKING' },
  IDLE: { color: '#4aa5ff', railClass: 'mcx-rail-cyan', label: 'IDLE' },
  ERROR: { color: '#e74c3c', railClass: 'mcx-rail-red', label: 'ERROR' },
  [RESIDUAL_BUCKET]: { color: '#88948a', railClass: 'mcx-rail-grey', label: 'OFF' }
}

/**
 * @param {string} bucket one of the four canonical `StatusBucket` values
 * @returns {{color: string, railClass: string, label: string}}
 */
function bucketMeta (bucket) {
  return BUCKET_META[bucket] ?? BUCKET_META[RESIDUAL_BUCKET]
}

/** @returns {string} the state-name code of this bucket — WORKING/IDLE/ERROR/OFF, the same
 * everywhere (chips, pill, filters, KPIs); screens translate it by code (story 204) */
function bucketLabelKey (bucket) {
  return bucketMeta(bucket).label
}

/** @returns {string} this bucket's Stitch accent colour (theme param kept for
 * signature compatibility with existing callers/tests; unused on purpose) */
function bucketColor (theme, bucket) {
  return bucketMeta(bucket).color
}

/** @returns {string} the Stitch severity-rail CSS class for this bucket */
function bucketRailClass (bucket) {
  return bucketMeta(bucket).railClass
}

// DISPLAY-ONLY mapping of the device's real status to a visual treatment, used
// while sun does not ship `statusBucket` yet (PO request: the card must show the
// REAL state, not the "Non-operative" degradation placeholder). This does NOT
// reimplement sun's canonical bucketing rule (RN3): filtering and KPIs keep going
// exclusively through `getDeviceBucket`; this only picks a chip label + accent.
// Real device statuses (PO): OFF(0), IDLE(1), WORKING(2), ERROR.
const STATUS_VISUAL_BUCKET = {
  WORKING: 'WORKING',
  IDLE: 'IDLE',
  ERROR: 'ERROR',
  OFF: RESIDUAL_BUCKET
}

/**
 * What the status chip should SHOW for a device: sun's bucket when present (its
 * state-name code — WORKING/IDLE/ERROR/OFF, PO correction, see BUCKET_META above, in
 * the 'StatusBucket' domain), otherwise the device's real raw status (a
 * 'DeviceStatusEnum' code, '' when there is none) styled by a display-only visual
 * mapping. Story 204 (S76): screens show `statusCode` translated by code, '—' when it is
 * ''. `labelKey` ('' always) and `rawLabel` stay in the return shape for their tests.
 * @param {{statusBucket?: string, status?: string}} device
 * @returns {{labelKey: string, rawLabel: string, bucket: string, statusCode: string, statusDomain: string}}
 */
function statusDisplay (device) {
  const sunBucket = normalizeSunBucket(device?.statusBucket)
  if (sunBucket !== '') {
    const label = bucketMeta(sunBucket).label
    return { labelKey: '', rawLabel: label, bucket: sunBucket, statusCode: label, statusDomain: 'StatusBucket' }
  }
  const status = device?.status ?? ''
  const visualBucket = STATUS_VISUAL_BUCKET[status] ?? RESIDUAL_BUCKET
  return { labelKey: '', rawLabel: status !== '' ? status : '—', bucket: visualBucket, statusCode: status, statusDomain: 'DeviceStatusEnum' }
}

// PLACEHOLDER production metrics for the production-mode card (PO: invented values
// for now; the real per-device fields — OEE, cycle time, parts today — are decided
// when building sun and will replace this helper, see sun-contract-proposal.md).
// TODO(sun-contract): read oee/cycleTime/partsToday from the deviceSummary item (or
// a sibling endpoint) instead of returning fixed dummies.
function productionPlaceholders (device) {
  return {
    placeholder: true,
    oee: '87%',
    cycle: '42s',
    partsToday: '128'
  }
}

// ---- production detail formatting (story 35, monitoring/deviceProduction) ----

/**
 * Local time-of-day `HH:mm` for an epoch-millis instant. Sun sends `0` (and we
 * normalize missing to `0`) when the timestamp is absent → returns '' so callers show
 * a dash. Mirrors the in-monitoring precedent (SensorAlarmList.formatTime).
 * @param {number} millis epoch millis (0/undefined = absent)
 * @returns {string}
 */
function formatClock (millis) {
  if (!millis) return ''
  const parsed = new Date(millis)
  if (isNaN(parsed.getTime())) return ''
  return parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

/**
 * Human elapsed time since an epoch-millis start (`Xh YYm` / `Ym YYs` / `Ys`),
 * computed at render time. '' when the start is absent. Mirrors the planner's
 * `formatDuration` idiom (PlannerProgressDisplay.jsx).
 * @param {number} startMillis epoch millis of the task/job start (0/undefined = absent)
 * @returns {string}
 */
function formatElapsedSince (startMillis) {
  if (!startMillis) return ''
  const totalSeconds = Math.max(0, Math.round((Date.now() - startMillis) / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`
  if (minutes > 0) return `${minutes}m ${String(seconds).padStart(2, '0')}s`
  return `${seconds}s`
}

/**
 * Clamp a driver telemetry percentage (sun sends it as a string like "72%") to a
 * 0–100 number for the progress bar width. Returns 0 when unparseable.
 * @param {string|number} raw
 * @returns {number}
 */
function percentValue (raw) {
  const parsed = parseFloat(String(raw))
  if (!Number.isFinite(parsed)) return 0
  return Math.max(0, Math.min(100, parsed))
}

export { bucketMeta, bucketLabelKey, bucketColor, bucketRailClass, statusDisplay, productionPlaceholders, formatClock, formatElapsedSince, percentValue }
